import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  InputLabel,
  ErrorText,
  ModalCancelButton,
  ModalSubmitButton,
} from '../../Navbar/modals/changePasswordModal.styles';
import { renameDocument } from '../../../services/application';
import { TextInput } from '../../UploadDocuments/uploadDocuments.styles';

const RenameDocumentModal = ({ isOpen, onClose, doc, applicationId }) => {
  const [name, setName] = useState('');
  const [errors, setErrors] = useState({});
  const overlayRef = useRef(null);

  const queryClient = useQueryClient();

  // Auto-fill the field whenever a new document is passed in / modal opens
  useEffect(() => {
    if (isOpen && doc) {
      setName(doc.name || '');
      setErrors({});
    }
  }, [isOpen, doc]);

  const mutation = useMutation({
    mutationFn: renameDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['application', applicationId] });
      window.alert('Document renamed successfully');
      handleClose();
    },
    onError: (err) => {
      setErrors({
        form:
          err?.response?.data?.message ||
          err?.message ||
          'Failed to rename document. Please try again.',
      });
    },
  });

  const handleClose = () => {
    setName('');
    setErrors({});
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!name.trim()) nextErrors.name = 'Name is required';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    mutation.mutate({
      documentId: doc?.id,
      applicationId,
      documentName: name.trim(),
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay
      onClick={handleClose}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rename-doc-modal-title"
      ref={overlayRef}
    >
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle id="rename-doc-modal-title">Rename document</ModalTitle>
          <ModalCloseButton onClick={handleClose} type="button" aria-label="Close">
            <X size={16} />
          </ModalCloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <InputLabel htmlFor="doc-name">Document name</InputLabel>
              <TextInput
                id="doc-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter document name"
                $hasError={!!errors.name}
                aria-describedby={errors.name ? 'doc-name-error' : undefined}
              />
              {errors.name && (
                <ErrorText id="doc-name-error" role="alert">
                  {errors.name}
                </ErrorText>
              )}
            </FormGroup>

            {errors.form && <ErrorText role="alert">{errors.form}</ErrorText>}
          </ModalBody>

          <ModalFooter>
            <ModalCancelButton type="button" onClick={handleClose}>
              Cancel
            </ModalCancelButton>
            <ModalSubmitButton type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              ) : null}
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </ModalSubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default RenameDocumentModal;
